import { useEffect, useState, useRef, Utils } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import { Line } from 'react-chartjs-2';
import ChartDataDrag from 'chartjs-plugin-dragdata';
import { useGesture } from '@use-gesture/react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartDataDrag,
    annotationPlugin
)

const options = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
        padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        },
        minWidth: 0,
    },
    interaction: {
        mode: 'nearest',
        intersect: false,
        axis: "xy",
    },
    scales: {
        x: {
            dragData: false,
            grid: {
                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.2)" : "rgb(255, 193, 78, 0.33)"
            },
            //ticks: {
            //    color: "white",
            //    font: {
            //        size: 14,
            //    }
            //},
            display: false,
            autoSkip: false,
        },
        y: {
            dragData: false,
            beginAtZero: false,
            //min: 200,
            //max: 1400,
            stepSize: 500,
            grid: {
                color: global.gw.theme === 'light' ? "rgba(45, 45, 68, 0.2)" : "rgb(255, 193, 78, 0.33)"
            },
            ticks: {
                color: global.gw.theme === 'light' ? "rgb(45, 45, 68)" : "white",
                font: {
                    size: 14,
                }
            },
        }
    },
    plugins: {
        legend: {
            display: false,
            position: "bottom",
            labels: {
                font: {
                    size: 14,
                },
                color: global.gw.theme === 'light' ? 'rgb(45, 45, 68)' : 'white',
            },
        },
        tooltips: {
            enabled: false,
            titleColor: global.gw.theme === 'light' ? "rgb(139, 0, 0)" : "red",
        },
        title: {
            display: false,
            color: global.gw.theme === 'light' ? "rgb(45, 45, 68)" : "rgb(244, 204, 142)",
            align: "center",
            fontSize: 40,
            position: "bottom",
            padding: {
                top: 10,
            }
        },
        annotation: {
            annotations: [],
        },
    },
};

const CarbonDioxieChartTile = ({ ...parameters }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [seedData, setSeedData] = useState([{
        labels: [],
        datasets: [],
    }]);
    const chartRef = useRef(null);
    const gesture = useGesture({
        onDragEnd: e => {
            if (chartRef && chartRef.current) {
                chartRef.current.tooltip?.setActiveElements([]);
            }
        },
    });


    let binId = parameters.binid;
    let deviceId = parameters.deviceid;
    let dateFrom = parameters.dateFrom;
    let dateTo = parameters.dateTo;
    let hourFrom = parameters.hourFrom;
    let hourTo = parameters.hourTo;

    useEffect(() => {
        (async () => {
            setSeedData([{
                labels: [],
                datasets: [],
            }]);
            await fetchWeatherStationReadings();
            window.scrollTo(0, 100);
        })();
    }, [dateFrom]);

    const fetchWeatherStationReadings = async () => {
        setIsLoading(true);

        if (!binId || binId == 0) {
            binId = null;
        }

        let jsonSettings = {
            deviceId: deviceId == "All" ? null : deviceId,
            readingTypeId: null,
            binId: binId,
        };

        options.plugins.annotation.annotations = [];

        let weatherStationReadingSettingsResponse = await createApiEndpoint().fetchWeatherStationReadingSettings("direct", jsonSettings);

        let weatherStationReadingSettings = null;
        if (weatherStationReadingSettingsResponse && weatherStationReadingSettingsResponse.data && weatherStationReadingSettingsResponse.data.getWeatherStationReadingSettings) {
            let arrayBuffer = global.base64StringToArrayBuffer(weatherStationReadingSettingsResponse.data.getWeatherStationReadingSettings);
            let decompressed = await global.decompress(arrayBuffer, "gzip");
            weatherStationReadingSettings = JSON.parse(decompressed);
        }

        if (!weatherStationReadingSettings) {
            setIsLoading(false);
            return;
        }

        if (!binId || binId == 0) {
            binId = null;
        }

        if (hourFrom < 0) {
            hourFrom = 0;
        }
        else if (hourFrom > 23) {
            hourFrom = 23;
        }

        let hourFromString = `${hourFrom}`;
        if (hourFrom.toString().length === 1) {
            hourFromString = `0${hourFrom}`;
        }

        if (hourTo < 0) {
            hourTo = 0;
        }
        else if (hourTo > 23) {
            hourTo = 23;
        }

        let hourToString = `${hourTo}`;
        if (hourTo.toString().length === 1) {
            hourToString = `0${hourTo}`;
        }

        let jsonReadings = {
            binId: binId,
            deviceId: deviceId == "All" ? null : deviceId,
            dateFrom: dateFrom.toJSON().split("T")[0] + `T${hourFromString}:00:00.000Z`,
            dateTo: global.dateToJsonCurrentTimeZone(dateTo),
        };

        let siteWeatherStationReadings = await createApiEndpoint().fetchSiteWeatherStationReadings("direct", jsonReadings);
        let siteBinCurrentWeatherStationReadings = global.gw.currentWeatherReadings.filter(x => x.siteId === parameters.siteid && x.binId === binId);

        let generatedDaysGroup = [];
        let hourlyDates = global.getDatesByHour(dateFrom);
        let generatedDeviceReadings = [];
        for (var h = 0; h < hourlyDates.length; h++) {
            let hourlyDate = hourlyDates[h].toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "");
            for (var d = 0; d < siteBinCurrentWeatherStationReadings.length; d++) {
                let deviceReading = siteBinCurrentWeatherStationReadings[d];
                generatedDeviceReadings.push({
                    BarometricPressure: deviceReading.BarometricPressure,
                    CarbonDioxide: deviceReading.CarbonDioxide,
                    DateRead: hourlyDate,
                    DeviceId: deviceReading.DeviceId,
                    DewPoint: deviceReading.DewPoint,
                    Emc: deviceReading.Emc,
                    Humidity: deviceReading.Humidity,
                    Mc: deviceReading.Mc,
                    StaticPressure: deviceReading.StaticPressure,
                    Temperature: deviceReading.Temperature,
                    WeatherStationDeviceId: deviceReading.WeatherStationDeviceId,
                    WeatherStationReadingId: deviceReading.WeatherStationReadingId,
                    RealDateRead: deviceReading.DateRead,
                });
            }
        }
        generatedDaysGroup = Object.groupBy(generatedDeviceReadings, ({ DateRead }) => DateRead);

        let daysGroup = [];
        let deviceReadings = [];
        if (siteWeatherStationReadings && siteWeatherStationReadings.data && siteWeatherStationReadings.data.getSiteWeatherStationReadings) {
            let arrayBuffer = global.base64StringToArrayBuffer(siteWeatherStationReadings.data.getSiteWeatherStationReadings);
            let decompressed = await global.decompress(arrayBuffer, "gzip");
            deviceReadings = JSON.parse(decompressed);

            let addedDeviceReadings = [];
            for (var d = 0; d < siteBinCurrentWeatherStationReadings.length; d++) {
                if (!deviceReadings.some(x => x.DeviceId === siteBinCurrentWeatherStationReadings[d].DeviceId)) {
                    for (var dr = 0; dr < deviceReadings.length; dr++) {
                        let deviceReading = deviceReadings[dr];
                        let generatedDeviceReading = generatedDeviceReadings.filter(x => x.DeviceId === siteBinCurrentWeatherStationReadings[d].DeviceId)[0];
                        addedDeviceReadings.push({
                            BarometricPressure: generatedDeviceReading.BarometricPressure,
                            CarbonDioxide: generatedDeviceReading.CarbonDioxide,
                            DateRead: deviceReading.DateRead,
                            DeviceId: generatedDeviceReading.DeviceId,
                            DewPoint: generatedDeviceReading.DewPoint,
                            Emc: generatedDeviceReading.Emc,
                            Humidity: generatedDeviceReading.Humidity,
                            Mc: generatedDeviceReading.Mc,
                            StaticPressure: generatedDeviceReading.StaticPressure,
                            Temperature: generatedDeviceReading.Temperature,
                            WeatherStationDeviceId: generatedDeviceReading.WeatherStationDeviceId,
                            WeatherStationReadingId: generatedDeviceReading.WeatherStationReadingId,
                            RealDateRead: generatedDeviceReading.DateRead,
                        });
                    }
                }
            }

            for (var i = 0; i < addedDeviceReadings.length; i++) {
                deviceReadings.push(addedDeviceReadings[i]);
            }

            daysGroup = Object.groupBy(deviceReadings, ({ DateRead }) => new Date(DateRead).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", ""));
        }
        else {
            deviceReadings = generatedDeviceReadings;
            daysGroup = generatedDaysGroup;
        }

        let labels = [];
        let datasets = [];

        if (!(siteWeatherStationReadings && siteWeatherStationReadings.data && siteWeatherStationReadings.data.getSiteWeatherStationReadings)) {
            siteWeatherStationReadings.data.getSiteWeatherStationReadings = deviceReadings;
        }

        if (siteWeatherStationReadings && siteWeatherStationReadings.data && siteWeatherStationReadings.data.getSiteWeatherStationReadings) {
            labels = Object.keys(daysGroup);

            const deviceIdGroup = Object.groupBy(deviceReadings, ({ DeviceId }) => DeviceId);
            const deviceIdList = Object.keys(deviceIdGroup);

            for (let l = 0; l < deviceIdList.length; l++) {
                let deviceId = deviceIdList[l];
                let binName = weatherStationReadingSettings.filter(x => x.DeviceId == deviceId)[0]?.BinName;
                let dataSet = {
                    label: `Bin: ${binName}\r\nDevice: ${deviceId}`,
                    data: [],
                    tension: 0.5,
                    binId: parameters.binid,
                    fill: false,
                    spanGaps: true,
                    pointRadius: 1,
                };
                datasets.push(dataSet);

                let sg = deviceIdGroup[deviceId];

                let mappedNumbers = deviceReadings.filter(x => x.DeviceId == deviceId).map(x => +x.CarbonDioxide);
                let maxCarbonDioxide = Math.max.apply(Math, mappedNumbers);

                let color = global.gw.theme === 'light' ? "rgb(34, 139, 34)" : "rgb(112, 255, 0)"; // Darker green in light mode

                let isNotRead = sg.some(x => x.RealDateRead);
                if (isNotRead) {
                    color = global.gw.theme === 'light' ? "rgb(139, 0, 0)" : "rgb(255, 0, 0)"; // Darker red in light mode
                    dataSet.label += ` ${sg[0].RealDateRead} `;
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.HighWarning &&
                    maxCarbonDioxide < weatherStationReadingSettings[0]?.ModerateLimit) {
                    color = global.gw.theme === 'light' ? "#b8860b" : "#ffff00"; // Darker yellow in light mode
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.ModerateLimit &&
                    maxCarbonDioxide < weatherStationReadingSettings[0]?.HighLimit) {
                    color = global.gw.theme === 'light' ? "#cc7000" : "#ffa500"; // Darker orange in light mode
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.HighLimit &&
                    maxCarbonDioxide < weatherStationReadingSettings[0]?.CriticalLimit) {
                    color = global.gw.theme === 'light' ? "#cc4500" : "#ff4500"; // Darker red-orange in light mode
                }
                else if (maxCarbonDioxide >= weatherStationReadingSettings[0]?.CriticalLimit) {
                    color = global.gw.theme === 'light' ? "#8b0000" : "#d32f2f"; // Darker red in light mode
                }

                dataSet.borderColor = color;
                dataSet.backgroundColor = dataSet.borderColor;

                for (let i = 0; i < labels.length; i++) {
                    let day = labels[i];
                    let ths = sg.filter(x => new Date(x.DateRead).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "") === day);

                    if (ths && ths.length > 0 && ths[0].CarbonDioxide > -40) {
                        dataSet.data.push(ths[0].CarbonDioxide);
                    }
                    else {
                        dataSet.data.push(null);
                    }
                }
            }

            let tempSeedData = seedData;
            tempSeedData = [{
                labels: labels,
                datasets: datasets,
            }];
            setSeedData(tempSeedData);
            setIsLoading(false);
        }
        else {
            setIsLoading(false);
        }
    }

    return (
        <MuiMaterials.Box
            className="carbonDioxideChart"
        >
            {isLoading ?
                <MuiMaterials.RotatingLines
                    strokeColor={global.gw.theme === 'light' ? "rgb(59, 62, 68)" : "#f4cc8e"}
                    strokeWidth="3"
                    animationDuration="0.72"
                    width="48"
                    visible={true}
                    sx={{
                        transform: "scale(0.8)",
                        transformOrigin: "center",
                        //marginTop: "-97px",
                        //marginLeft: "-14px",
                        //transformOrigin: "top",
                        //transform: "scale(0.75)",
                    }}
                />
                :
                <Line
                    className={"carbonDioxideLine"}
                    data={seedData[0]}
                    options={options}
                    ref={chartRef}
                    onMouseOut={(event) => {
                        if (chartRef && chartRef.current) {
                            chartRef.current.tooltip?.setActiveElements([]);
                        }

                        //if (!chartElement.length) {
                        // If the cursor is not over a data point, hide the tooltip
                        // event.chart.tooltip?.setActiveElements([]);
                        //}
                    }}
                    onMouseLeave={(event) => {
                        if (chartRef && chartRef.current) {
                            chartRef.current.tooltip?.setActiveElements([]);
                        }

                        //if (!chartElement.length) {
                        // If the cursor is not over a data point, hide the tooltip
                        // event.chart.tooltip?.setActiveElements([]);
                        //}
                    }}
                    {...gesture()}
                />
            }
            </MuiMaterials.Box>
    );
}
export default CarbonDioxieChartTile;